import clsx from 'clsx'
import {
  Button,
  CreateTableState,
  Grid,
  Icon,
  Tooltip,
  Typography,
} from 'gu-ui-kit'
import React, { useEffect, useState } from 'react'
import styles from 'modules/Agency/agency.module.scss'
import { ArchiveToggleButton } from 'modules/Agency/Clients/subcomponents/ArchiveToggleButton'
import formatMoney from 'utils/formatMoney'
import PrioritySwitcher from 'modules/Agency/Clients/subcomponents/PrioritySwitcher'
import StatusBadge from 'modules/Agency/Requests/subcomponents/StatusBadge'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import DropdownComponent from 'components/Dropdown'
import EditableField from 'components/EditableField'

export const useTableConfig = (
  { setPriority, switchUser, onFetchSubRows, applyAccountName, ...rest },
  data = [],
) => {
  const { t } = useTranslation()

  const breakpoints = useBreakpoint()

  const [columnVisibility, setColumnVisibility] = useState({})

  useEffect(() => {
    switch (true) {
      case breakpoints.sm:
        setColumnVisibility({ status: false, balance: false })
        break
      case breakpoints.fb:
        setColumnVisibility({ status: false })
        break
      default:
        setColumnVisibility({})
        break
    }
  }, [breakpoints])

  const tableConfig = {
    className: styles['clients-list'],
    state: {
      columnVisibility,
    },
    columns: [
      {
        id: 'expand',
        header: () => ' ',
        cell: ({
          // eslint-disable-next-line react/prop-types
          row,
        }) => (
          <Grid wrap={'nowrap'} justifyContent="start" alignItems="center">
            <Grid fluid={false} alignItems="center" justifyContent="center">
              <Button
                noLabel
                startIcon={
                  <Icon
                    name={row.getIsExpanded() ? 'arrow-up' : 'arrow1-down'}
                  />
                }
                title={t('agency::list.icon_title', {
                  context: row.getIsExpanded() ? 'close' : 'open',
                })}
                color={
                  row.getIsExpanded() ? 'Interface-Nine' : 'Interface-Seventh'
                }
                bgColor={row.getIsExpanded() ? 'Background-Tertiary' : 'none'}
                bgColorHover={
                  row.getIsExpanded() ? 'Background-Tertiary' : 'none'
                }
                bgColorActive={
                  row.getIsExpanded() ? 'Background-Tertiary' : 'none'
                }
                size={'m'}
                onClick={() => {
                  row.toggleExpanded(!row.getIsExpanded())
                  onFetchSubRows(row.original.id)
                }}
              />
            </Grid>
          </Grid>
        ),
      },
      {
        id: 'priority',
        header: () => (
          <Grid wrap={'nowrap'} justifyContent="start" alignItems="center">
            <Grid fluid={false} alignItems="center" justifyContent="center">
              <Button
                startIcon={<Icon name={'flag'} />}
                bgColorHover={'none'}
                bgColorActive={'none'}
                bgColor={'none'}
                color={'text-secondary'}
                size={'m'}
              />
            </Grid>
          </Grid>
        ),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              id,
              // eslint-disable-next-line react/prop-types
              priority, // eslint-disable-next-line react/prop-types
            },
          },
        }) => (
          <Grid wrap={'nowrap'} justifyContent="start" alignItems="center">
            <PrioritySwitcher
              priority={priority}
              onChange={(newPriority) => {
                setPriority(id, newPriority)
              }}
            />
          </Grid>
        ),
      },
      {
        id: 'name',
        header: () =>
          t('agency::list.headers.name', {
            context: breakpoints.sm
              ? 'with_status_balance'
              : breakpoints.fb
              ? 'with_status'
              : '',
          }),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              id,
              // eslint-disable-next-line react/prop-types
              status,
              // eslint-disable-next-line react/prop-types
              name,
              // eslint-disable-next-line react/prop-types
              user: { email, balance },
            },
          },
        }) => (
          <div style={{ width: '100%' }}>
            <Grid wrap={'nowrap'} justifyContent="start" alignItems="center">
              <Grid
                fluid={false}
                direction="column"
                alignItems="start"
                justifyContent="start"
                className={styles['clients-list__col-name']}
              >
                <Typography
                  variant={'p7'}
                  noMargin
                  color={'text-primary'}
                  className={styles['client__name']}
                  component={
                    <EditableField
                      className={styles['clients-list__edit-account-name']}
                      id={id}
                      name={name}
                      login={name}
                      apply={applyAccountName}
                      editIconClassName={styles['edit-account__edit-icon']}
                    >
                      <div>{name}</div>
                    </EditableField>
                  }
                />

                <Typography
                  variant={'p11'}
                  noMargin
                  color={'text-secondary'}
                  className={styles['client__email']}
                >
                  {email}
                </Typography>
                {breakpoints.sm && (
                  <Typography
                    noMargin
                    variant={'p11'}
                    className={styles['client__balance_small']}
                  >
                    {balance.length > 0
                      ? balance.map(({ amount, currency }, index) => (
                          <span
                            key={'balance-' + id + '-' + index}
                            dangerouslySetInnerHTML={{
                              __html: formatMoney(amount, currency),
                            }}
                          />
                        ))
                      : '—'}
                  </Typography>
                )}
                {breakpoints.fb && status === 'archived' && (
                  <StatusBadge status={status} />
                )}
                {/*</Container>*/}
              </Grid>
            </Grid>
          </div>
        ),
      },
      {
        id: 'status',
        header: () => t('agency::list.headers.status'),
        className: clsx(styles['clients-list__col_status']),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              status, // eslint-disable-next-line react/prop-types
            },
          },
        }) => (
          <Grid
            alignItems="start"
            justifyContent="start"
            direction="column"
            l={{
              direction: 'row',
              alignItems: 'center',
              justifyContent: 'between',
            }}
          >
            {status === 'archived' && <StatusBadge status={status} />}
          </Grid>
        ),
      },
      {
        id: 'balance',
        header: () => t('agency::list.headers.balance'),
        className: styles['clients-list__col-balance'],
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              id,
              // eslint-disable-next-line react/prop-types
              user: { balance },
            },
          },
        }) => (
          // eslint-disable-next-line react/prop-types
          <div className={styles['clients-list__col-balance-layout']}>
            {/* eslint-disable-next-line react/prop-types */}
            {balance.length > 0
              ? balance.map(({ amount, currency }, index) => (
                  <span
                    className={styles['clients-list__balance-row']}
                    key={'balance-' + id + '-' + index}
                    dangerouslySetInnerHTML={{
                      __html: formatMoney(amount, currency),
                    }}
                  />
                ))
              : '—'}
          </div>
        ),
      },
      {
        id: 'actions',
        isPlaceholder: true,
        className: styles['clients-list__col-actions'],
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              id, // eslint-disable-next-line react/prop-types
              name, // eslint-disable-next-line react/prop-types
              status, // eslint-disable-next-line react/prop-types
              user: { email },
            },
          },
        }) => (
          <Grid
            alignItems="flex-end"
            justifyContent="flex-end"
            className={styles['clients-list__col-actions-layout']}
          >
            {!breakpoints.fb ? (
              <>
                <ArchiveToggleButton
                  asIconButton={true}
                  className={styles['clients-list__action-icon-button']}
                  action={status === 'active' ? 'archive' : 'activate'}
                  id={id}
                  login={name}
                />

                <Tooltip title={t(`agency::list.actions.login_as_user`)}>
                  <Button
                    noLabel
                    startIcon={<Icon name={'logout'} />}
                    size={'m'}
                    onClick={() => switchUser(email)}
                    color={'Label-GU-Primary'}
                  />
                </Tooltip>
              </>
            ) : (
              <DropdownComponent
                wrapItems={true}
                ButtonComponent={() => (
                  <Button
                    noLabel
                    startIcon={<Icon name={'dropdown-menu'} />}
                    color={'Interface-GU-Primary'}
                    size={'m'}
                    className={styles['dropTriggerIcon']}
                  />
                )}
              >
                <ArchiveToggleButton
                  asIconButton={false}
                  className={styles['clients-list__action-icon-button']}
                  action={status === 'active' ? 'archive' : 'activate'}
                  id={id}
                  login={name}
                />

                <Button
                  color={'Interface-Seventh'}
                  variant={'link'}
                  onClick={() => switchUser(email)}
                  size={'m'}
                  startIcon={
                    <Icon
                      name={'logout'}
                      size={'m'}
                      color={'Interface-GU-Primary'}
                    />
                  }
                >
                  {t('agency::list.actions.login_as_user')}
                </Button>
              </DropdownComponent>
            )}
          </Grid>
        ),
      },
    ],
    ...rest,
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
